import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, CheckCircle2, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  business_name: "", city: "", category: "",
  contact_info: "", sender_name: "", sender_email: "", message: "",
};

const CATS = ["Restaurant","Alimentation","Beauté & Bien-être","Mode","Automobile","Maison",
  "Bricolage","Services","Artisans","Loisirs","Sport","Enfants & Famille","Animaux","Voyages",
  "Culture","Hôtel","Autre"];

export default function RecommendPartner() {
  useSeo({
    title: "Recommander un commerce local en Eure-et-Loir | PROXIreduc-28",
    description: "Vous appréciez un commerçant ou un artisan de l'Eure-et-Loir ? Recommandez-le sur PROXIreduc-28 pour l'aider à gagner en visibilité locale. C'est gratuit et rapide.",
    canonicalPath: "/recommander-un-commerce",
    image: "/og-image.jpg",
  });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.city) {
      toast.error("Nom du commerce et ville sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/partner-recommendations", form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="recommend-page">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold uppercase tracking-widest">
          <Heart size={14}/> Faites grandir le réseau
        </div>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold text-slate-900" data-testid="recommend-title">
          Recommandez un commerce
        </h1>
        <p className="mt-2 text-slate-600">
          Vous connaissez un commerce ou un artisan qui devrait rejoindre PROXIreduc-28 ? Dites-le nous, nous le contacterons !
        </p>
      </div>

      {done ? (
        <div className="mt-10 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center" data-testid="recommend-done">
          <CheckCircle2 className="mx-auto text-emerald-700" size={40}/>
          <div className="mt-3 font-heading text-xl font-extrabold text-emerald-900">Merci pour votre recommandation !</div>
          <p className="mt-2 text-emerald-800 text-sm">Notre équipe prend contact avec ce commerce prochainement.</p>
          <Button onClick={() => setDone(false)} className="mt-5 btn-shine text-white" data-testid="recommend-again">
            Recommander un autre commerce
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nom du commerce <span className="text-rose-500">*</span></Label>
              <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                     placeholder="Ex: La Boulangerie du Bourg" required
                     data-testid="recommend-name"/>
            </div>
            <div>
              <Label>Ville <span className="text-rose-500">*</span></Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)}
                     placeholder="Ex: Chartres" required
                     data-testid="recommend-city"/>
            </div>
          </div>

          <div>
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="recommend-category"><SelectValue placeholder="Choisir une catégorie..."/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Coordonnées du commerce (téléphone, site, adresse...)</Label>
            <Input value={form.contact_info} onChange={(e) => set("contact_info", e.target.value)}
                   placeholder="Ex: 02 37 00 00 00 ou boulangerie.com"
                   data-testid="recommend-contact"/>
            <p className="mt-1 text-xs text-slate-500">Facultatif — tout ce qui peut nous aider à les contacter rapidement.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
            <div>
              <Label>Votre nom</Label>
              <Input value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)}
                     placeholder="Facultatif"
                     data-testid="recommend-sender-name"/>
            </div>
            <div>
              <Label>Votre email</Label>
              <Input type="email" value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)}
                     placeholder="Facultatif — pour vous tenir informé"
                     data-testid="recommend-sender-email"/>
            </div>
          </div>

          <div>
            <Label>Message (facultatif)</Label>
            <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)}
                      placeholder="Pourquoi ce commerce ? Avez-vous un contact ? Précisez ici..."
                      data-testid="recommend-message"/>
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={saving} className="btn-shine text-white h-11 px-6" data-testid="recommend-submit">
              {saving ? <><Loader2 size={14} className="mr-1 animate-spin"/> Envoi...</> : <><Store size={14} className="mr-1"/> Envoyer la recommandation</>}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
